import * as React from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import {
  getFamilySpecieCount,
  disableFamilyById,
  requestFamilyHardDelete,
  confirmFamilyHardDelete,
} from '../../../services'
import { setIsStored } from '../../../redux/slices/Alerts'
import VerificationCodeDialog from '../../VerificationCodeDialog'
import {
  setPendingDelete,
  getPendingDelete,
  clearPendingDelete,
} from '../../../utils/pendingDelete'

const PENDING_TABLE = 'family'

const isSuccessfulResponse = result => {
  if (!result) return false
  const hasExplicitFailure =
    result?.update === false ||
    result?.updated === false ||
    result?.success === false ||
    result?.deleted === false ||
    Boolean(result?.error) ||
    Boolean(result?.errors)
  return !hasExplicitFailure
}

/**
 * Flujo de eliminación de familia a nivel de tabla (pre-check de especies +
 * OTP). Controlado de forma imperativa (`ref.openModal(family)`) para sobrevivir
 * a refetch/re-render del DataGrid mientras el usuario lee el código en su correo.
 */
const DeleteFamilyModal = React.forwardRef((_props, ref) => {
  const dispatch = useDispatch()
  const [family, setFamily] = React.useState(null)

  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [dialogLoading, setDialogLoading] = React.useState(false)
  const [dialogMode, setDialogMode] = React.useState('confirmDelete')
  const [dialogMessage, setDialogMessage] = React.useState('')
  const [associatedSpecieCount, setAssociatedSpecieCount] = React.useState(null)

  const [otpDialogOpen, setOtpDialogOpen] = React.useState(false)
  const [otpLoading, setOtpLoading] = React.useState(false)
  const [otpError, setOtpError] = React.useState(null)
  const [resendCount, setResendCount] = React.useState(0)
  const [retryAfterSeconds, setRetryAfterSeconds] = React.useState(null)

  const runPrecheck = React.useCallback(async targetFamily => {
    setDialogMode('confirmDelete')
    setDialogMessage('')
    setAssociatedSpecieCount(null)
    setDialogOpen(true)
    setDialogLoading(true)

    try {
      const result = await getFamilySpecieCount(targetFamily.id)
      const count = result?.count ?? null

      if (result?.error || count === null) {
        setDialogMode('confirmDelete')
        setDialogMessage(result?.message || 'No se pudo verificar especies asociadas.')
      } else if (count > 0) {
        setAssociatedSpecieCount(count)
        setDialogMode('suggestDisable')
        setDialogMessage(
          `La familia tiene ${count} especie(s) asociada(s), por lo que no puede eliminarse físicamente.`,
        )
      } else {
        setAssociatedSpecieCount(0)
        setDialogMode('confirmDelete')
        setDialogMessage('')
      }
    } catch {
      setDialogMode('confirmDelete')
      setDialogMessage('No se pudo verificar especies asociadas.')
    } finally {
      setDialogLoading(false)
    }
  }, [])

  React.useImperativeHandle(ref, () => ({
    openModal: targetFamily => {
      if (!targetFamily?.id) return
      setFamily(targetFamily)
      setOtpError(null)
      runPrecheck(targetFamily)
    },
  }))

  // Restaurar el OTP tras una recarga (sin re-solicitar ni repetir el pre-check).
  React.useEffect(() => {
    const pending = getPendingDelete(PENDING_TABLE)
    if (pending) {
      setFamily({ id: pending.id, nombre: pending.label })
      setOtpDialogOpen(true)
    }
  }, [])

  const handleDialogClose = () => {
    if (dialogLoading) return
    setDialogOpen(false)
    setDialogMode('confirmDelete')
    setDialogMessage('')
    setAssociatedSpecieCount(null)
  }

  const handleDisable = async () => {
    if (!family) return
    setDialogLoading(true)
    try {
      const result = await disableFamilyById(family.id)
      if (!isSuccessfulResponse(result)) {
        dispatch(setIsStored({ stored: false, message: result?.message || 'No se pudo deshabilitar la familia.' }))
      } else {
        dispatch(setIsStored({ stored: true, message: result?.message || 'Familia deshabilitada correctamente.' }))
      }
    } catch {
      dispatch(setIsStored({ stored: false, message: 'Error al deshabilitar la familia.' }))
    } finally {
      setDialogLoading(false)
      setDialogOpen(false)
      setDialogMode('confirmDelete')
      setDialogMessage('')
      setAssociatedSpecieCount(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!family) return
    setDialogLoading(true)
    setDialogOpen(false)

    const result = await requestFamilyHardDelete(family.id)
    setDialogLoading(false)

    if (result?.cooldownActive) {
      setRetryAfterSeconds(result.retryAfter ?? null)
      setOtpDialogOpen(true)
      setPendingDelete({ table: PENDING_TABLE, id: family.id, label: family.nombre })
    } else if (result?.requested) {
      setRetryAfterSeconds(null)
      setResendCount(c => c + 1)
      setOtpDialogOpen(true)
      setPendingDelete({ table: PENDING_TABLE, id: family.id, label: family.nombre })
    } else {
      dispatch(setIsStored({ stored: false, message: result?.message || 'No se pudo solicitar la eliminación.' }))
    }
  }

  const handleRequestNewCode = async () => {
    if (!family) return
    const result = await requestFamilyHardDelete(family.id)
    if (result?.cooldownActive) {
      setRetryAfterSeconds(result.retryAfter ?? null)
      setOtpError('Espera antes de reenviar.')
    } else if (result?.requested) {
      setRetryAfterSeconds(null)
      setResendCount(c => c + 1)
      setOtpError(null)
    }
  }

  const handleOtpConfirm = async code => {
    if (!family) return
    setOtpLoading(true)
    setOtpError(null)
    const result = await confirmFamilyHardDelete(family.id, code)
    setOtpLoading(false)

    if (result?.deleted) {
      setOtpDialogOpen(false)
      clearPendingDelete()
      dispatch(setIsStored({ stored: true, message: 'Familia eliminada permanentemente.' }))
    } else {
      setOtpError(result?.message || 'Código inválido o expirado.')
    }
  }

  const handleOtpClose = () => {
    if (otpLoading) return
    setOtpDialogOpen(false)
    setOtpError(null)
    clearPendingDelete()
  }

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle color="primary.main" fontWeight={600}>
          {dialogLoading
            ? 'Procesando...'
            : dialogMode === 'suggestDisable'
              ? 'No se puede eliminar físicamente'
              : 'Eliminar familia'}
        </DialogTitle>

        <DialogContent>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={32} />
            </Box>
          ) : dialogMode === 'suggestDisable' ? (
            <DialogContentText>
              {dialogMessage || 'La familia tiene especies asociadas y no puede eliminarse permanentemente.'}
              <br /><br />
              Puedes <strong>deshabilitar la familia</strong> para mantener integridad de datos.
              Las especies y árboles asociados no se eliminarán.
            </DialogContentText>
          ) : (
            <DialogContentText>
              ¿Eliminar permanentemente la familia <strong>{family?.nombre}</strong>?
              <br /><br />
              Se enviará un código de verificación a tu correo para confirmar.
              {dialogMessage ? <><br /><br />{dialogMessage}</> : null}
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={dialogLoading}>
            Cancelar
          </Button>
          {!dialogLoading && dialogMode === 'confirmDelete' && (
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              Continuar
            </Button>
          )}
          {!dialogLoading && dialogMode === 'suggestDisable' && (
            <Button onClick={handleDisable} variant="contained" color="warning">
              {associatedSpecieCount > 0
                ? `Deshabilitar familia (${associatedSpecieCount} especies asociadas)`
                : 'Deshabilitar familia'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <VerificationCodeDialog
        open={otpDialogOpen}
        onClose={handleOtpClose}
        onConfirm={handleOtpConfirm}
        onRequestNew={handleRequestNewCode}
        loading={otpLoading}
        resourceLabel={family?.nombre || ''}
        error={otpError}
        resendCount={resendCount}
        retryAfterSeconds={retryAfterSeconds}
      />
    </>
  )
})

DeleteFamilyModal.displayName = 'DeleteFamilyModal'

export default DeleteFamilyModal
